import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { QuoteProvider } from "@/lib/quote";
import SmoothScroll from "@/components/SmoothScroll";
import PromoBar from "@/components/PromoBar";
import Home from "@/pages/Home";
import ServicePage from "@/pages/ServicePage";
import ServicesDirectory from "@/pages/ServicesDirectory";
import { CalculatorPage, FaqPage, ContactPage } from "@/pages/Sections";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import CrmApp from "@/crm/CrmApp";
import PublicEstimate from "@/crm/PublicEstimate";
import { PrivacyPolicy, TermsOfService, CookiePolicy } from "@/pages/Legal";
import BookingConfirmed from "@/pages/BookingConfirmed";
import QuickBook from "@/pages/QuickBook";
import NotFound from "@/pages/NotFound";

function Gone() {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center bg-[#08080b] text-white px-6 text-center">
      <div className="font-mono-alt text-[10px] tracking-widest text-white/30 uppercase mb-3">410 — Gone</div>
      <h1 className="font-display text-2xl mb-2">This page no longer exists</h1>
      <p className="text-white/50 text-sm">The page you are looking for has been permanently removed.</p>
      <a href="/" className="mt-6 px-5 py-2.5 rounded-full border border-white/15 text-sm text-white/70 hover:text-white hover:border-white/40 transition-colors">
        Back to homepage
      </a>
    </div>
  );
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

// Lenis momentum scroll is a public-site nicety — inside the CRM it hijacks
// wheel events and breaks inner scrolling (dropdowns, modal lists). Public
// marketing pages keep it; the CRM, the booking flow and the public estimate
// page get native, predictable scrolling.
function PublicOnlySmoothScroll() {
  const { pathname } = useLocation();
  const isTool = pathname.startsWith("/crm") || pathname.startsWith("/book")
    || pathname.startsWith("/estimate/") || pathname.startsWith("/booking-confirmed");
  if (isTool) return null;
  return <SmoothScroll />;
}

function App() {
  return (
    <div className="App min-h-screen bg-[#0c0c0f]">
      <BrowserRouter>
        <QuoteProvider>
          <PublicOnlySmoothScroll />
          <ScrollManager />
          <PromoBar />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<ServicesDirectory />} />
            <Route path="/book" element={<QuickBook />} />
            <Route path="/services/:slug" element={<ServicePage />} />
            <Route path="/booking-confirmed" element={<BookingConfirmed />} />
            <Route path="/calculator" element={<CalculatorPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            {/* Legacy admin panel is gone — old /admin/* URLs must never
                redirect anywhere; nginx answers 410 before this SPA loads,
                this route is the same hard 404 for non-nginx environments. */}
            <Route path="/admin/*" element={<Gone />} />
            <Route path="/crm/*" element={<CrmApp />} />
            <Route path="/estimate/:token" element={<PublicEstimate />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/:slug" element={<ServicePage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster
            position="bottom-center"
            theme="dark"
            toastOptions={{
              style: {
                background: "#121216",
                border: "1px solid rgba(255,255,255,0.10)",
                color: "#ffffff",
              },
            }}
          />
        </QuoteProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
